import { Link, NavLink, Outlet } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Footer } from './Footer';
import { PageGuide } from './PageGuide';

const navItems = [
  { to: '/proof', label: 'Proof' },
  { to: '/services', label: 'Services' },
  { to: '/articles', label: 'Articles' },
  { to: '/about', label: 'About' },
];

const COMPACT_HEADER_ENTER_SCROLL = 40;
const COMPACT_HEADER_EXIT_SCROLL = 0;

function useWideCompactHeader() {
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 768px)');
    let frame = 0;

    const update = () => {
      setIsCompact((current) => {
        if (!media.matches) {
          return false;
        }

        const scrollY = window.scrollY || window.pageYOffset || 0;

        if (!current && scrollY >= COMPACT_HEADER_ENTER_SCROLL) {
          return true;
        }

        if (current && scrollY <= COMPACT_HEADER_EXIT_SCROLL) {
          return false;
        }

        return current;
      });
    };

    const scheduleUpdate = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    media.addEventListener('change', scheduleUpdate);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', scheduleUpdate);
      media.removeEventListener('change', scheduleUpdate);
    };
  }, []);

  return isCompact;
}

export function Layout() {
  const isCompact = useWideCompactHeader();
  const navLinkBase = cn(
    'flex shrink-0 items-center rounded-md px-3 text-sm text-ink/70 transition-colors duration-150 hover:bg-ink/5 hover:text-ink',
    isCompact ? 'h-8' : 'h-9',
  );

  return (
    <div className="flex min-h-screen flex-col">
      <header
        className={cn(
          'sticky top-0 z-50 border-b border-ink/5 bg-paper/95 shadow-[0_1px_18px_rgba(11,13,16,0.04)] backdrop-blur supports-[backdrop-filter]:bg-paper/85',
          isCompact && 'shadow-[0_1px_16px_rgba(11,13,16,0.08)]',
        )}
      >
        <nav
          className={cn('mx-auto max-w-5xl px-4 sm:px-6', isCompact ? 'py-2' : 'py-3')}
          aria-label="Primary"
        >
          <div className="flex items-center justify-between gap-3">
            <Link
              to="/"
              className={cn('shrink-0 font-display tracking-tight', isCompact ? 'text-base' : 'text-lg')}
            >
              JL Patawaran
            </Link>
            <div className="hidden min-w-0 items-center gap-2 md:flex">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    cn(navLinkBase, isActive && 'bg-ink/5 text-ink')
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Button asChild size="sm" className="ml-1 shrink-0 px-4">
                <Link to="/work-with-me#prefit">Check fit</Link>
              </Button>
            </div>
            <Button asChild size="sm" className="shrink-0 px-3 md:hidden">
              <Link to="/work-with-me#prefit">
                <span>Fit</span>
              </Link>
            </Button>
          </div>
          <div className="-mx-1 mt-3 flex min-w-0 gap-1 overflow-x-auto pb-1 md:hidden">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(navLinkBase, isActive && 'bg-ink/5 text-ink')
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>
      <main className="flex-1">
        <PageGuide />
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
